import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import CenteredCircularProgress from '../../../../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from '../../../../../../../../lib/designSystem/HeaderWithActions';
import {
  getByStakeholder,
  getManagementsGroupedBy,
} from '../../../../../../../../lib/api/collections';
import NewManagement from '../../../Documents/components/NewManagement';
import NewMultipleManagement from '../../../Documents/components/NewMultipleManagement';
import DocumentSelector from './components/DocumentSelector/indext';
import { loggerDev } from '../../../../../../../../lib/functions/utils';
import Info from '@mui/icons-material/Info';
import NetworkBusinessDetails from '../../../../../../../Network/components/Businesses/components/Details';
import { LightTooltip } from '../../../../../../../../lib/designSystem/WhiteTooltip';
import { Typography } from '@mui/material';
import EnhancedTable from '../../../../../../../../lib/designSystem/Table';
import GlobalSearcher, {
  Filter,
} from '../../../../../../../../lib/common/GlobalSearcher';
import ManagementsDetails from './components/ManagementsDetails';

const CollectionsManagementsGroupedBy = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [collectionsData, setCollectionsData] = useState<any[]>([]);
  const [executiveOptions, setExecutiveOptions] = useState<any[]>([]);

  const endDateDefault = new Date();
  endDateDefault.setUTCHours(23, 59, 59, 999);
  const PAGE_SIZE = 10;

  const [businessForDetails, setBusinessForDetails] = useState<any>(null);
  const [openBusinessDetails, setOpenBusinessDetails] = useState(false);
  const [byStakeholder, setByStakeholder] = useState<boolean>(true);

  const [openDocumentSelector, setOpenDocumentSelector] = useState(false);
  const [openCreateNewManagement, setOpenCreateNewManagement] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [openCreateNewManagementMultiple, setOpenCreateNewManagementMultiple] =
    useState(false);
  const [documentsForMultiple, setDocumentsForMultiple] = useState<any[]>([]);
  const [businessForSearch, setBusinessForSearch] = useState<string>('');
  const [executiveId, setExecutiveId] = useState<string>('all');

  const [openManagements, setOpenManagements] = useState(false);
  const [businessForManagements, setBusinessForManagements] =
    useState<any>(null);

  const handleShowManagements = (id: number) => {
    const row: any = collectionsData.find((x: any) => x.id === id);
    if (!row) return;
    setBusinessForManagements({
      identifier: row.businessIdentifier,
      name: row.businessName,
    });
    setOpenManagements(true);
  };

  const actions: {
    action: (id: number) => void;
    actionLabel: string;
  }[] = [
    {
      action: handleShowManagements,
      actionLabel: 'Ver gestiones',
    },
  ];

  const generateQuery = () => {
    let query = '';
    if (businessForSearch) query += `businessIdentifier=${businessForSearch}`;
    if (executiveId && executiveId !== 'all')
      query += `executiveId=${executiveId}&`;
    return query;
  };

  const searchCollectionsGroupedBy = async () => {
    setLoading(true);
    try {
      const query = generateQuery();
      const response = await getManagementsGroupedBy(dispatch, query);
      setCollectionsData(response.data);
      setExecutiveOptions(response.executiveOptions);
    } catch (error) {
      loggerDev(error);
    }
    setLoading(false);
  };

  const handleShowBusinessDetails = (id: string) => {
    const row: any = collectionsData.find((x: any) => x.id === id);
    if (!row) return;
    setBusinessForDetails({
      id: row.businessIdentifier,
      name: row.businessName,
    });
    setOpenBusinessDetails(true);
  };

  const headers: {
    key: string;
    label: string;
    type?: any;
    icon?: any;
    handleAction?: any;
  }[] = [
    { key: 'businessIdentifier', label: 'Rut' },
    {
      key: 'businessName',
      label: byStakeholder ? 'Deudor' : 'Cliente',
      handleAction: handleShowBusinessDetails,
      icon: <Info fontSize="small" />,
    },
    {
      key: 'collectionManagementsAmount',
      label: 'Gestiones sin completar',
    },
    {
      key: 'closestCollectionManagementDate',
      label: 'Fecha vencimiento más próxima',
      type: 'due-date',
    },
  ];

  const reloadPage = () => {
    setOpenCreateNewManagement(false);
    setOpenCreateNewManagementMultiple(false);
    setOpenManagements(false);
    setOpenBusinessDetails(false);
    setOpenDocumentSelector(false);
    setSelectedDocument(null);
    setBusinessForManagements(null);
    setBusinessForDetails(null);
    searchCollectionsGroupedBy();
  };

  const handleConfirmSelection = (document: any) => {
    setSelectedDocument(document);
    loggerDev(document);
    setOpenDocumentSelector(false);
    setOpenCreateNewManagement(true);
  };

  const handleConfirmSelectionMultiple = (documents: any) => {
    setDocumentsForMultiple(documents);
    setOpenDocumentSelector(false);
    setOpenCreateNewManagementMultiple(true);
  };

  const handleOpenCreateNewManagementFromCompletion = (documentId: string) => {
    setOpenManagements(false);
    setSelectedDocument({ id: documentId });
    setOpenCreateNewManagement(true);
  };

  const handleOpenCreateNewMultiManagementFromCompletion = (
    documentIds: string[],
  ) => {
    const documentsWithIds = documentIds.map((id) => {
      return { id: id };
    });
    setOpenManagements(false);
    setDocumentsForMultiple(documentsWithIds);
    setOpenCreateNewManagementMultiple(true);
  };

  useEffect(() => {
    searchCollectionsGroupedBy();
  }, []);

  const getIfByStakeholder = async () => {
    try {
      const response = await getByStakeholder(dispatch);
      setByStakeholder(response.byStakeholder);
    } catch (error) {
      loggerDev(error);
    }
  };

  useEffect(() => {
    getIfByStakeholder();
  }, []);

  const createButton: CustomButtonProps = {
    name: 'Crear gestión',
    action: () => setOpenDocumentSelector(true),
    color: 'primary',
    hidden: loading,
  };

  const title = (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <Typography variant="body1" fontWeight="bold">
        {`Gestiones sin completar ${byStakeholder ? 'por deudor' : 'por cliente'}`}
      </Typography>
      <LightTooltip
        title={
          <Typography padding={1}>
            Puedes buscar un {byStakeholder ? 'deudor ' : 'cliente '}
            específico en el buscador.
          </Typography>
        }
      >
        <Info fontSize="small" style={{ marginLeft: 4 }} />
      </LightTooltip>
    </div>
  );

  const filters: Filter[] = [
    {
      label: byStakeholder ? 'Rut deudor' : 'Rut cliente',
      type: 'text',
      value: businessForSearch,
      setter: setBusinessForSearch,
    },
    {
      label: 'Ejecutivo',
      type: 'select',
      value: executiveId,
      setter: setExecutiveId,
      options: [{ label: 'Todos', value: 'all' }, ...executiveOptions],
    },
  ];

  return (
    <div>
      <HeaderWithActions
        title={title}
        variant={'body1'}
        fontWeight="bold"
        buttons={[
          {
            name: 'Filtro',
            action: () => {},
            color: 'secondary',
            customButton: (
              <GlobalSearcher
                filters={filters}
                triggerSearch={searchCollectionsGroupedBy}
              />
            ),
          },
          createButton,
        ]}
      />
      {loading ? (
        <CenteredCircularProgress relative />
      ) : (
        <EnhancedTable
          dataHeaders={headers}
          exportToFile="xlsx"
          data={collectionsData}
          rowsPerPageDefault={PAGE_SIZE}
          actions={actions}
          showActions
        />
      )}
      {openCreateNewManagement && (
        <NewManagement
          openModal={openCreateNewManagement}
          setOpenModal={setOpenCreateNewManagement}
          documentId={selectedDocument?.id}
          setUp={reloadPage}
        />
      )}
      {openCreateNewManagementMultiple && (
        <NewMultipleManagement
          openModal={openCreateNewManagementMultiple}
          setOpenModal={setOpenCreateNewManagementMultiple}
          setUp={reloadPage}
          documentsWithId={documentsForMultiple}
        />
      )}
      {openDocumentSelector && (
        <DocumentSelector
          open={openDocumentSelector}
          setOpen={setOpenDocumentSelector}
          handleConfirmSelection={handleConfirmSelection}
          handleConfirmSelectionMultiple={handleConfirmSelectionMultiple}
        />
      )}
      {businessForDetails && (
        <NetworkBusinessDetails
          open={openBusinessDetails}
          setOpen={setOpenBusinessDetails}
          business={businessForDetails}
        />
      )}
      {openManagements && (
        <ManagementsDetails
          open={openManagements}
          setOpen={setOpenManagements}
          business={businessForManagements}
          byStakeholder={byStakeholder}
          reloadPage={reloadPage}
          handleOpenCreateNewManagementFromCompletion={
            handleOpenCreateNewManagementFromCompletion
          }
          handleOpenCreateNewMultiManagementFromCompletion={
            handleOpenCreateNewMultiManagementFromCompletion
          }
        />
      )}
    </div>
  );
};

export default CollectionsManagementsGroupedBy;
